import { useEffect, useState } from 'react'
import { useApp } from '../context/AppContext'
import { AnimatePresence, motion } from 'framer-motion'
import { sileo } from 'sileo'

// Components
import SettingsSection from '../components/Settings/SettingsSection'
import StatusTable from '../components/status/StatusTable'
import StatusAdd from '../components/status/StatusAdd'
import StatusEdit from '../components/status/StatusEdit'
import OffCanvas from '../components/Shared/OffCanvas'
import Modal from '../components/Shared/Modal'

// Hooks
import useOffCanvas from '../hooks/useOffCanvas'
import useModal from '../hooks/useModal'

const TicketStatus = () => {
  const { supabase } = useApp()

  const { isOpen, config, close } = useOffCanvas()
  const {
    openModal,
    isOpen: isModalOpen,
    modalConfig,
    closeModal,
    isLargeModal,
  } = useModal()

  const [statuses, setStatuses] = useState([])
  const [loading, setLoading] = useState(false)

  // ==========================================
  // GET
  // ==========================================
  const fetchStatuses = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .schema('Tickets')
        .from('status')
        .select('*')
        .order('id', { ascending: true })

      if (error) throw error
      setStatuses(data || [])
    } catch (err) {
      console.error(err)
      sileo.error({
        title: 'Error de conexión',
        description: 'No pudimos obtener los estados.',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStatuses()
  }, [])

  // ==========================================
  // CREATE
  // ==========================================
  const handleAdded = () => {
    closeModal()
    fetchStatuses()
  }

  // ==========================================
  // UPDATE
  // ==========================================
  const handleUpdateStatus = async (id, payload) => {
    try {
      const { error } = await supabase
        .schema('Tickets')
        .from('status')
        .update(payload)
        .eq('id', id)

      if (error) throw error

      sileo.success({
        title: 'Estado actualizado',
        description: 'Los cambios se guardaron correctamente.',
      })

      closeModal()
      fetchStatuses()
    } catch (error) {
      console.error(error)
      sileo.error({
        title: 'Error',
        description: 'No se pudo actualizar el estado.',
      })
    }
  }

  // ==========================================
  // EDIT MODAL
  // ==========================================
  const handleEdit = (status) => {
    openModal(
      'Editar Estado',
      <StatusEdit status={status} onSave={handleUpdateStatus} />
    )
  }

  return (
    <div>
      {/* OVERLAYS */}
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            key="offcanvas-wrapper"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-backdrop show"
          >
            <OffCanvas isOpen={isOpen} onClose={close} title={config.title}>
              <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
              >
                {config.component}
              </motion.div>
            </OffCanvas>
          </motion.div>
        )}

        {isModalOpen && (
          <motion.div
            key="modal-wrapper"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Modal
              isOpen={isModalOpen}
              onClose={closeModal}
              title={modalConfig.title}
              isLarge={isLargeModal}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
              >
                {modalConfig.component}
              </motion.div>
            </Modal>
          </motion.div>
        )}
      </AnimatePresence>

      <SettingsSection
        title="Estados del ticket"
        icon="fa-circle-half-stroke"
        description="Etapas del flujo de trabajo y su orden de aparición."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() =>
              openModal('Agregar Estado', <StatusAdd onAdded={handleAdded} />)
            }
          >
            <i className="fa-solid fa-plus me-2" />
            Nuevo estado
          </button>
        }
      >
        <StatusTable
          data={statuses}
          loading={loading}
          onRefresh={fetchStatuses}
          onEdit={handleEdit}
        />
      </SettingsSection>
    </div>
  )
}

export default TicketStatus
